import {
  Box,
  Button,
  Card,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { FileDownload } from "@mui/icons-material";
import * as XLSX from "xlsx";

export interface Column<T> {
  key: keyof T | string;
  label: string;
  render?: (row: T) => React.ReactNode;
  exportValue?: (row: T) => string | number | null;
  excelFormat?: string;
}

/** Monta uma aba a partir de `columns`/`data`, com o `excelFormat` aplicado às células numéricas de cada coluna. */
function montarAba<T>(columns: Column<T>[], data: T[]) {
  const header = columns.map((col) => col.label);
  const rows = data.map((row) =>
    columns.map((col) =>
      col.exportValue ? col.exportValue(row) : ((row[col.key as keyof T] as unknown as string | number) ?? ""),
    ),
  );
  const sheet = XLSX.utils.aoa_to_sheet([header, ...rows]);
  columns.forEach((col, c) => {
    if (!col.excelFormat) return;
    rows.forEach((_, r) => {
      const cell = sheet[XLSX.utils.encode_cell({ r: r + 1, c })];
      if (cell && typeof cell.v === "number") cell.z = col.excelFormat;
    });
  });
  return sheet;
}

/** Grava `data` num .xlsx a partir de `columns` — mesma lógica usada pelo botão "Exportar Excel" do DataTable, reutilizável por tabelas que não usam o componente (ex.: árvores custom). */
export function exportTableToExcel<T>(columns: Column<T>[], data: T[], fileName: string, sheetName = "Dados") {
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, montarAba(columns, data), sheetName);
  XLSX.writeFile(workbook, `${fileName}.xlsx`);
}

/** Nome de aba aceito pelo Excel: até 31 caracteres, sem `\ / ? * [ ] :`, não vazio e único dentro do arquivo. */
export function nomeDeAba(nome: string, usados: Set<string>) {
  const base = nome.replace(/[\\/?*[\]:]/g, " ").replace(/\s+/g, " ").trim().slice(0, 31) || "Aba";
  let candidato = base;
  for (let i = 2; usados.has(candidato.toLowerCase()); i++) {
    const sufixo = ` (${i})`;
    candidato = base.slice(0, 31 - sufixo.length) + sufixo;
  }
  usados.add(candidato.toLowerCase());
  return candidato;
}

/** Como `exportTableToExcel`, mas com uma aba por grupo (`nome` vira o nome da aba) e as mesmas colunas em todas. */
export function exportTablesToExcel<T>(columns: Column<T>[], grupos: { nome: string; data: T[] }[], fileName: string) {
  const workbook = XLSX.utils.book_new();
  const usados = new Set<string>();
  grupos.forEach((grupo) => XLSX.utils.book_append_sheet(workbook, montarAba(columns, grupo.data), nomeDeAba(grupo.nome, usados)));
  XLSX.writeFile(workbook, `${fileName}.xlsx`);
}

interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  total: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rows: number) => void;
  actions?: (row: T) => React.ReactNode;
  onRowClick?: (row: T) => void;
  /** Mostra um botão "Exportar Excel" que grava `data` (só a página atual, já que a tabela pagina no servidor). */
  exportToExcel?: boolean;
  /** Nome do arquivo gerado, sem extensão. Padrão: "dados". */
  excelFileName?: string;
  /** Conteúdo renderizado à esquerda, na mesma linha do botão de exportar (ex.: o `<FilterModal>` da página) — sem isso, o botão fica sozinho alinhado à direita. */
  toolbar?: React.ReactNode;
  /** Linha fixa no fim da tabela (ex.: totais), indexada pela `key` da coluna; só no desktop. */
  footer?: Partial<Record<string, React.ReactNode>>;
}

function MobileCardList<T>({
  data,
  columns,
  actions,
  onRowClick,
}: Pick<DataTableProps<T>, "data" | "columns" | "actions" | "onRowClick">) {
  return (
    <Stack spacing={1.5}>
      {data.map((row, index) => (
        <Card
          key={index}
          elevation={0}
          onClick={onRowClick ? () => onRowClick(row) : undefined}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 3,
            bgcolor: "background.paper",
            overflow: "hidden",
            cursor: onRowClick ? "pointer" : "default",
          }}
        >
          <Box sx={{ p: 2 }}>
            <Stack spacing={1.25}>
              {columns.map((col) => (
                <Box key={String(col.key)}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "grey.400",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      display: "block",
                      mb: 0.25,
                    }}
                  >
                    {col.label}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "text.primary", fontWeight: 500 }}
                  >
                    {col.render
                      ? col.render(row)
                      : String(row[col.key as keyof T] ?? "-")}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>

          {actions && (
            <>
              <Divider sx={{ borderColor: "divider" }} />
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  display: "flex",
                  justifyContent: "center",
                  bgcolor: "background.default",
                }}
              >
                {actions(row)}
              </Box>
            </>
          )}
        </Card>
      ))}
    </Stack>
  );
}

export function DataTable<T>({
  data,
  columns,
  total,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  actions,
  onRowClick,
  exportToExcel,
  excelFileName,
  toolbar,
  footer,
}: DataTableProps<T>) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleExportToExcel = () => exportTableToExcel(columns, data, excelFileName ?? "dados");

  return (
    <Box sx={{ width: "100%" }}>
      {(toolbar || exportToExcel) && (
        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1.5} sx={{ mb: 1.5 }}>
          <Box>{toolbar}</Box>
          {exportToExcel && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<FileDownload fontSize="small" />}
              onClick={handleExportToExcel}
              disabled={data.length === 0}
            >
              Exportar Excel
            </Button>
          )}
        </Stack>
      )}
      {isMobile ? (
        <MobileCardList data={data} columns={columns} actions={actions} onRowClick={onRowClick} />
      ) : (
        <Box sx={{ overflowX: "auto" }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {columns.map((col) => (
                    <TableCell
                      key={String(col.key)}
                      sx={{ whiteSpace: "nowrap", fontWeight: 600 }}
                    >
                      {col.label}
                    </TableCell>
                  ))}
                  {actions && (
                    <TableCell
                      align="right"
                      sx={{ whiteSpace: "nowrap", fontWeight: 600 }}
                    >
                      Ações
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((row, index) => (
                  <TableRow
                    key={index}
                    hover
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    sx={{ cursor: onRowClick ? "pointer" : "default" }}
                  >
                    {columns.map((col) => (
                      <TableCell key={String(col.key)} sx={{ whiteSpace: "nowrap" }}>
                        {col.render
                          ? col.render(row)
                          : (row[col.key as keyof T] as any)}
                      </TableCell>
                    ))}
                    {actions && (
                      <TableCell align="right" onClick={(e) => e.stopPropagation()} sx={{ whiteSpace: "nowrap" }}>
                        {actions(row)}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
              {footer && data.length > 0 && (
                <TableFooter>
                  <TableRow>
                    {columns.map((col) => (
                      <TableCell
                        key={String(col.key)}
                        sx={{ whiteSpace: "nowrap", fontWeight: 700, fontSize: "0.875rem", color: "text.primary" }}
                      >
                        {footer[String(col.key)]}
                      </TableCell>
                    ))}
                    {actions && <TableCell />}
                  </TableRow>
                </TableFooter>
              )}
            </Table>
          </TableContainer>
        </Box>
      )}

      <TablePagination
        rowsPerPageOptions={isMobile ? [5, 10] : [5, 10, 25]}
        component="div"
        count={total}
        rowsPerPage={rowsPerPage}
        labelRowsPerPage={isMobile ? "" : "Linhas por página"}
        page={page}
        onPageChange={(_, p) => onPageChange(p)}
        onRowsPerPageChange={(e) =>
          onRowsPerPageChange(parseInt(e.target.value, 10))
        }
        sx={
          isMobile
            ? {
                ".MuiTablePagination-selectLabel": { display: "none" },
                ".MuiTablePagination-displayedRows": { fontSize: "0.8rem" },
                mt: 1,
              }
            : {}
        }
      />
    </Box>
  );
}
